import React, { FC } from "react";
import styled from "styled-components";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { Button } from "@components/atoms/Button";
import { rgba } from "polished";
import worldIcon from "@iconify/icons-whh/world";
import { Icon } from "@iconify/react";
import HtmlParser from "react-html-parser";
import { breakpoints } from "@theme/breakpoints";
import { Link } from "gatsby";

const StyledWrapper = styled.div`
  border-radius: 20px;
  background: ${({ theme }) => theme.background};
  padding: 35px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.washLight};

  ${breakpoints.tabletMax`
    width: 100%;
  `}

  ${breakpoints.phoneMax`
    padding: 25px;
  `}
`;

const StyledName = styled.h3`
  margin: 0 0 25px;
  font-size: 2.8rem;
  font-weight: 700;

  ${breakpoints.laptopMax`
    font-size: 2.4rem;
  `}

  ${breakpoints.phoneMax`
    font-size: 2.2rem;
  `};

  @media (max-width: 420px) {
    text-align: center;
  }
`;

const StyledTagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  @media (max-width: 420px) {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledTag = styled.div<{ $color: string }>`
  padding: 6px 16px;
  background: ${({ $color }) => rgba($color, 0.1)};
  color: ${({ $color }) => rgba($color, 0.8)};
  border-radius: 6px;
  font-weight: 700;

  ${breakpoints.phoneMax`
    font-size: 1.4rem;
  `};
`;

const StyledDescription = styled.div`
  margin: 35px 0;
  font-size: 1.8rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.washStrong};
  text-align: justify;

  b {
    font-weight: 600;
  }

  p {
    margin: 0;
  }

  ${breakpoints.laptopMax`
    font-size: 1.6rem;
  `}
`;

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: auto;
`;

const StyledLocation = styled.div`
  display: flex;
  align-items: center;
  color: #985100;
  margin-bottom: 12px;

  svg {
    margin-right: 10px;
  }

  @media (max-width: 420px) {
    justify-content: center;
  }
`;

export const Position: FC<Props> = ({
  name,
  tags,
  description,
  className,
  href,
}) => {
  const { t } = useTranslation();
  const { t: ct } = useTranslation("contact");

  return (
    <StyledWrapper className={className}>
      <StyledLocation>
        <Icon icon={worldIcon} />
        {t("remote")}
      </StyledLocation>
      <StyledName>{name}</StyledName>
      <StyledTagsWrapper>
        {tags.map(({ content, color }, index) => (
          <StyledTag $color={color} key={index}>
            {content}
          </StyledTag>
        ))}
      </StyledTagsWrapper>
      <StyledDescription>{HtmlParser(description)}</StyledDescription>
      <StyledButton forwardedAs={Link as any} to={href}>
        {t("find-out-more")}
      </StyledButton>
    </StyledWrapper>
  );
};

interface Props {
  name: string;
  tags: { color: string; content: string }[];
  description: string;
  className?: string;
  href: string;
}
